import React, { useEffect, useRef } from 'react';
import { Table, Loading } from 'Components/nui';
import { useHistory, useLocation } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import qs from 'qs';
import Header from './Header';
import Filter from './Filter';
import Helmet from 'Components/Helmet';
import { useStoreState } from '~/store';
import { FiltersProvider } from 'Components/filters/context';
import { FormattedMessage, useIntl } from 'react-intl';
import RowToggle from 'Components/nui/RowToggle';
import useTable from './hooks/useTable';
import MarketRow from 'Pages/MarketRow';
import { useFilteredMarketOrders } from './hooks/useMarketOrders';

const MarketCell = ({ children, className, ...props }: any) =>
  className?.includes('col-product-name-alt') ? (
    <td className={className} {...props}>
      <div className="tw:flex tw:items-start">{children}</div>
    </td>
  ) : (
    <td className={className} {...props}>
      {children}
    </td>
  );

const marketComponents = { body: { cell: MarketCell } };

export const Body = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { pathname } = useLocation();
  const solution = useStoreState((state) => state.auth.solution);
  const storageKey = solution?.id ?? 'default';

  const [sortDirection, setSortDirection] = useLocalStorage(
    `market-sorting_direction_${storageKey}`,
    'down'
  );
  const [sortColumn, setSortColumn] = useLocalStorage(
    `market-sorting_column_${storageKey}`,
    'created'
  );

  const { data, isLoading, participants } = useFilteredMarketOrders(
    sortDirection ?? 'down',
    sortColumn ?? 'created'
  );

  const { columns, expandedRowKeys, setExpandedRowKeys } = useTable(
    [
      'product-brand',
      'product-name',
      'legend-price',
      'legend-volume',
      'legend-type',
      'ref-price-content',
      'state-tradeable',
      'origins',
      'locations',
      'incoterms',
      'etd-from',
      'etd-to',
      'loading-desc',
      'payment-term',
      'published',
    ],
    data,
    sortColumn ?? 'created',
    sortDirection ?? 'down',
    setSortColumn,
    setSortDirection,
    participants
  );

  const scrolled = useRef(false);
  useEffect(() => {
    if (!scrolled.current && data && data.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const orderParamId = urlParams.get('order');

      if (orderParamId) {
        requestAnimationFrame(() => {
          const element = document.querySelector(
            `tr[data-row-key="${orderParamId}"]`
          );
          if (element) {
            scrolled.current = true;
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.length]);

  if (isLoading || data === null)
    return (
      <div className="nui-row full-page-messaging align-center mt-10 pt-100 p-100">
        <Loading size="huge" />
      </div>
    );

  if (data.length === 0)
    return (
      <div className="nui-row full-page-messaging align-center mt-10 pt-100 p-100">
        <div className="icon-inbox bordered align-center">
          <FormattedMessage
            id="marketplace-empty-text"
            description="Text for when marketplace is empty"
            defaultMessage="There are currently no offers or bids. Place an offer or bid to start trading."
          />
        </div>
      </div>
    );

  return (
    <div className="full-page-content">
      <Table
        className="md-table generic-table expand-table"
        components={marketComponents}
        columns={columns}
        data={data}
        expandIconAsCell={false}
        expandIcon={({ expanded, onExpand, record }) => (
          <RowToggle
            open={expanded}
            onClick={(e) => {
              onExpand(record, e);
            }}
          ></RowToggle>
        )}
        rowKey={(record) => record.pid}
        rowClassName={(record) => {
          let name = expandedRowKeys.includes(record.pid) ? 'selected' : '';
          if (!record.hasProductAccess) {
            name = name + ' viewonly';
          }
          return name;
        }}
        expandedRowRender={(record) => (
          <MarketRow id={record.id} updated={record.updated} />
        )}
        expandedRowKeys={expandedRowKeys}
        onExpand={(_, record) => {
          if (expandedRowKeys.includes(record.pid)) {
            setExpandedRowKeys([0]);
            history.push(pathname);
          } else {
            setExpandedRowKeys([record.pid]);
            history.push(
              `${pathname}?${qs.stringify({
                order: record.pid,
              })}`
            );
          }
        }}
        onRow={(record) => {
          const isExpanded = expandedRowKeys.includes(record.pid);
          return {
            title: isExpanded
              ? formatMessage({
                  id: 'click-row-hide-details',
                  defaultMessage: 'Click row to hide details',
                })
              : formatMessage({
                  id: 'click-row-view-details',
                  defaultMessage: 'Click row to view details',
                }),
            'data-id': record.id,
          };
        }}
      />
    </div>
  );
};

const Market = () => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const marketplace = useStoreState(
    (state) => state.auth.solutionSettings.marketplace
  );
  const solution = useStoreState((state) => state.auth.solution);

  useEffect(() => {
    if (!marketplace) {
      history.replace('/tenders');
    }
  }, [marketplace, history]);

  if (!marketplace) return null;

  return (
    <div className="market">
      <Helmet>
        <title>
          {formatMessage({
            id: 'marketplace',
            defaultMessage: 'Marketplace',
          })}
        </title>
      </Helmet>

      <FiltersProvider store={`market-filters_${solution?.id}`}>
        <Header />
        <Filter />
        <Body />
      </FiltersProvider>
    </div>
  );
};

export default Market;
